import React, { useEffect, useState } from "react";
import { Heading, Subheading } from "../../design/heading";
import { Navbar, NavbarItem } from "../../design/navbar";
import { useParams } from "react-router-dom";
import { useMatterType } from "../../contexts/MatterTypeContext";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import MatterTypeIntakeQuestionList from "./components/MatterTypeIntakeQuestionList/MatterTypeIntakeQuestionList"; // This component handles the intake questions for the matter type
import MatterTypeQuestionList from "./components/MatterTypeQuestionList";
import EventTypeList from "./components/EventTypeList/EventTypeList";
import DeleteMatterTypeButton from "./components/DeleteMatterTypeButton";
import EditMatterTypeButton from "./components/EditMatterTypeButton";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Avatar } from "../../design/avatar";
import Breadcrumbs from "../../components/Breadcrumbs";
import MatterTypeMatterList from "./components/MatterTypeMatterList";

const MatterTypeDetailPage: React.FC = () => {
  const { matterTypeId } = useParams<{ matterTypeId: string }>();
  const { selectedMatterType, setSelectedMatterType, loading, error } =
    useMatterType();
  const [hasChanges, setHasChanges] = useState(false);
  const [state, setState] = useState("Intake Script");

  useEffect(() => {
    if (matterTypeId) {
      setSelectedMatterType(matterTypeId);
    }
  }, [matterTypeId]);

  if (loading.details) {
    return <LoadingView title="Loading Matter Type" />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedMatterType) {
    return <ErrorView error={new Error("Matter Type not found")} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs
        items={[
          { label: "Matter Types", href: "/matter-types" },
          {
            label: selectedMatterType.name,
            href: `/matter-type/${selectedMatterType._id}`,
          },
        ]}
      />
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {selectedMatterType.photoUrl && (
            <Avatar
              src={selectedMatterType.photoUrl}
              alt={selectedMatterType.name}
              className="w-12 h-12 rounded-full"
            />
          )}
          <Heading>{selectedMatterType.name}</Heading>
        </div>
        <div className="flex gap-4">
          <EditMatterTypeButton matterType={selectedMatterType} />
          {matterTypeId && (
            <DeleteMatterTypeButton matterTypeId={matterTypeId} />
          )}
        </div>
      </div>
      <DescriptionList>
        {selectedMatterType.description && (
          <>
            <DescriptionTerm>Description</DescriptionTerm>
            <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
              {selectedMatterType.description}
            </DescriptionDetails>
          </>
        )}
        {selectedMatterType.qualifications && (
          <>
            <DescriptionTerm>Qualifications</DescriptionTerm>
            <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
              {selectedMatterType.qualifications}
            </DescriptionDetails>
          </>
        )}
      </DescriptionList>
      <Navbar className="mb-4">
        {["Intake Script", "Event Types", "Matters"].map((item) => (
          <NavbarItem
            key={item}
            className="text-base"
            onClick={() => setState(item)}
            current={state === item}
          >
            {item}
          </NavbarItem>
        ))}
      </Navbar>
      {state === "Intake Script" && <MatterTypeIntakeQuestionList />}
      {state === "Event Types" && <EventTypeList />}
      {state === "Matters" && <MatterTypeMatterList />}
    </div>
  );
};

export default MatterTypeDetailPage;
